import { FC, useState, useCallback } from "react";
import { ExportApi } from "@/api/ExportApiV2";
import Logger from "@/utils/Logger";
import ExportResultQueryParams from "@/interfaces/ExportResultQueryParams";
import { Toast } from "@/components/Common/Toast/Toast";

export type ExportMode = "all" | "filtered";

interface ExportButtonProps {
    readonly mode: ExportMode;
    readonly searchParams?: ExportResultQueryParams;
    readonly className?: string;
    readonly onExportStart?: () => void;
    readonly onExportSuccess?: () => void;
    readonly onExportError?: (error: unknown) => void;
}

/**
 * Export Button Component - Common reusable component
 * Handles both "Export All" and "Export Results" functionality with transparent background styling
 *
 * @example
 * // Export All mode
 * <ExportButton mode="all" />
 *
 * @example
 * // Export Filtered Results mode
 * <ExportButton
 *   mode="filtered"
 *   searchParams={{ commodity: ["Avocado"], country: ["USA"] }}
 * />
 */
const ExportButton: FC<ExportButtonProps> = ({
    mode,
    searchParams,
    className = "",
    onExportStart,
    onExportSuccess,
    onExportError,
}) => {
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(async (): Promise<void> => {
        try {
            setIsExporting(true);
            onExportStart?.();

            Logger.info(`Starting ${mode} export process`, ["ExportButton"]);

            const { data, filename } =
                mode === "all"
                    ? await ExportApi.fetchExportCSV()
                    : await ExportApi.fetchExportResultsCSV(searchParams ?? {});

            // Create download link
            const url = window.URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Logger.info(`${mode} export completed successfully`, ["ExportButton"]);

            onExportSuccess?.();

            Toast.success(`Exported: ${filename}`);
        } catch (error) {
            Logger.error(`Error exporting ${mode}`, error, ["ExportButton"]);

            const errorMessage =
                error instanceof Error ? error.message : "Failed to export data";
            Toast.error(`Export failed: ${errorMessage}`);

            onExportError?.(error);
        } finally {
            setIsExporting(false);
        }
    }, [mode, searchParams, onExportStart, onExportSuccess, onExportError]);

    const buttonText = mode === "all" ? "Export All" : "Export Results";
    const buttonId = mode === "all" ? "export-button" : "export-results-button";
    const ariaLabel =
        mode === "all" ? "Export Sample Data" : "Export Results Data";
    const title =
        mode === "all"
            ? "Export facility details for all map"
            : "Export facility details for current search results";

    return (
        <div className="flex justify-center items-center">
            <button
                type="button"
                id={buttonId}
                aria-label={ariaLabel}
                className={`flex items-center gap-2 px-4 py-2 bg-transparent hover:bg-blue-50 border-none transition-colors ${className} ${isExporting ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    }`}
                onClick={() => {
                    void handleExport();
                }}
                disabled={isExporting}
                title={title}
            >
                {isExporting ? (
                    <span className="icon-sync animate-spin text-blue-600 text-2xl" />
                ) : (
                    <span className="icon-cloud_download text-blue-600 text-2xl" />
                )}
                <span className="text-blue-600 text-base font-medium">
                    {isExporting ? "Exporting..." : buttonText}
                </span>
            </button>
        </div>
    );
};

export default ExportButton;
